import * as THREE from 'three'
import { SpotLightCone } from './spotlightCone'
import { Group, Mesh, MeshStandardMaterial, Scene, SphereGeometry } from 'three'
import type { Object3D } from 'three'

interface SpotLight {
    spotLight: THREE.SpotLight
    spotLightAngle: number
    spotLightDistance: number
    spotLightIntensity: number
    group: Group
    positioningGroup: Group
    spotLightGroup: Group
    sphere: Mesh<SphereGeometry, MeshStandardMaterial>
    root: Group | Scene
}

class SpotLight implements SpotLight {
    constructor(
        root: Group | Scene
    ) {
        this.spotLightAngle = 25
        this.spotLightDistance = 20 //distance * intensity = total output
        this.spotLightIntensity = 30
        this.root = root
        this.initialize()
    }

    /**
     * Sets up all 3D groups and models and adds them to the root scene or group
     */
    initialize() {
        this.initializeGroups()
        this.initializeSpotlight()
        this.initializeCone()
        this.initializeSphere()
        this.group.rotation.set(0, 0, 0) //TODO Check if needed
        this.root.add(this.group)
    }

    /**
     * Sets up all 3D groups
     */
    initializeGroups() {
        this.group = new THREE.Group() //Main group to be added to root
        this.positioningGroup = new THREE.Group() //Group used to offset position
        this.spotLightGroup = new THREE.Group() //Contains spotlight

        this.positioningGroup.position.set(-0.68, -0.5, -1.32)

        this.positioningGroup.add(this.spotLightGroup)
        this.group.add(this.positioningGroup)
        this.root.add(this.group)
    }

    /**
     * Sets up 3D spotlight and spotlight properties
     */
    initializeSpotlight() {
        this.spotLight = new THREE.SpotLight(0xffd769, 1)
        this.spotLight.name = "spotlight"
        //Positioning
        this.spotLight.position.set(0, 0, 0)
        this.spotLight.rotation.set(1, 1, 1)
        this.spotLight.target.position.set(0, 0, -100)
        this.spotLight.target.updateWorldMatrix()
        //Visual
        this.spotLight.intensity = this.spotLightIntensity
        this.spotLight.angle = THREE.MathUtils.degToRad(this.spotLightAngle) //set to camera FOV
        this.spotLight.distance = this.spotLightDistance
        this.spotLight.castShadow = true
        this.spotLight.shadow.mapSize.width = 512
        this.spotLight.shadow.mapSize.height = 512
        this.spotLight.shadow.focus = 1
        this.spotLight.shadow.camera.near = 0.1
        this.spotLight.shadow.camera.far = 100
        this.spotLight.shadow.camera.fov = 1
        this.spotLightGroup.add(this.spotLight)
        this.spotLightGroup.position.set(0.675, 0.5, 1.325) //TODO Research magic numbers and replace
        this.spotLightGroup.rotation.set(0, 0, 0)
    }

    /**
     * Sets up 3D sphere and sphere properties
     */
    initializeSphere() {
        const geometry = new THREE.SphereGeometry(0.2, 12, 8)
        const material = new THREE.MeshStandardMaterial({ color: 0xffff00, transparent: true, opacity: 0 })
        const sphere = new THREE.Mesh(geometry, material)
        sphere.position.set(0, 0, 0)
        sphere.rotation.set(0, 0, 0)
        this.sphere = sphere
        this.spotLightGroup.add(this.sphere)
    }

    /**
     * Sets up spotlight cone
     */
    initializeCone() {
        this.positioningGroup.add(new SpotLightCone().group)
    }

    /**
     * Sets the spotlight position based on the given Object3D object position
     * @param object ThreeJS Object3D object
     */
    setSpotLightPositionByObject(object: Object3D) {
        this.group.position.set(object.position.x, object.position.y, object.position.z)
    }

    /**
     * Sets the spotlight rotation based on the gicen Object3D object rotation
     * @param object ThreeJS Object3D object
     */
    setSpotLightRotationByObject(object: Object3D) {
        const newSpotLightDirection = new THREE.Vector3()
        object.getWorldDirection(newSpotLightDirection)
        newSpotLightDirection.multiplyScalar(100)

        this.group.rotation.copy(object.rotation)
        this.spotLight.target.position.set(
            -newSpotLightDirection.x,
            -newSpotLightDirection.y,
            -newSpotLightDirection.z
        )
        //TODO Research proper arguments to pass into UpdateWorldMatrix
        this.spotLight.target.updateWorldMatrix()
    }

    /**
     * Sets the spotlight position and rotation based on the given Object3D object position and rotation
     * @param object ThreeJS Object3D object
     */
    setSpotLightPositionAndRotationByObject(object: Object3D) {
        this.setSpotLightPositionByObject(object)
        this.setSpotLightRotationByObject(object)
    }
}

export { SpotLight }